import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { payWithRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import ClosedFeature from "@/components/ClosedFeature";
import { FileText, ArrowRight, AlertCircle, Info, Calendar } from "lucide-react";

export default function AdministrativeFee() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartRegId = searchParams.get("cart_reg_id") || "";
  const [submitting, setSubmitting] = useState(false);
  const [dashData, setDashData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [unpaidReg, setUnpaidReg] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => {
      setDashData(r.data);
      const regs = r.data?.registrations || [];
      setUnpaidReg(regs.find(rg => rg.payment_status !== "paid") || null);
      setDataLoaded(true);
    }).catch(() => { setDataLoaded(true); });
  }, [user]);

  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-[#0B1429]">
        <Navbar />
        <div className="pt-28 pb-20 max-w-4xl mx-auto px-6 lg:px-10 space-y-4">
          <div className="h-8 w-24 bg-white/[0.03] animate-pulse rounded" />
          <div className="h-12 w-64 bg-white/[0.03] animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (dataLoaded && user?.role !== "admin") {
    const hasEventsInCart = !!unpaidReg;
    const hasPaidAcc = !!dashData?.accommodations?.some(a => a.payment_status === "paid");
    if (!hasEventsInCart && !hasPaidAcc && (dashData?.registrations || []).length === 0) {
      return (
        <ClosedFeature
          featureName="Administrative Fee"
          icon={Calendar}
          subtitle="SELECT EVENTS FIRST"
          badgeLabel={null}
          description="Please complete your event selection first. Once you've chosen your events, you'll be directed here to choose your accommodation."
        />
      );
    }
  }

  const confirmFee = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/accommodations/administrative-fee");

      if (cartRegId) {
        toast.success("Administrative fee added to cart. Proceeding to checkout...");
        navigate(`/payment?registration_id=${cartRegId}&booking_id=${data.booking_id}`);
      } else {
        const result = await payWithRazorpay({ booking_id: data.booking_id }, user);
        if (result) {
          toast.success("Administrative fee paid successfully!");
        }
        navigate("/dashboard");
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to add administrative fee");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-3xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">Opt-Out Option</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-2">
          Administrative <span className="text-[#00F0FF]">Fee</span>
        </h1>
        <p className="text-[#94A3B8] mb-8">
          A one-time $75 fee applies for participants who choose not to stay at an official partner hotel.
        </p>

        <div className="card-dark p-6 md:p-8 mb-8 border border-[#2A3A5F]">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center shrink-0 mt-1">
              <FileText className="w-5 h-5 text-white/70" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2">What does this fee cover?</h2>
              <p className="text-sm text-[#CBD5E1] leading-relaxed mb-4">
                The Asian Masters Swimming Championship partners with local hotels to provide a streamlined experience including guaranteed transport, security, and full-board meals.
              </p>
              <p className="text-sm text-[#CBD5E1] leading-relaxed">
                If you choose to arrange your own accommodation, this $75 fee covers the additional administrative overhead and venue processing required for independent arrivals.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FBBF24]/[0.08] border border-[#FBBF24]/20 mb-6">
            <AlertCircle className="w-5 h-5 text-[#FBBF24] mt-0.5 shrink-0" />
            <p className="text-sm text-[#CBD5E1]">
              <strong className="text-[#FBBF24]">Important:</strong> By paying this fee, you will <strong>not</strong> receive airport transfers, daily hotel-to-pool shuttles, or included meals. You are responsible for your own transport to and from the venue each day.
            </p>
          </div>

          <div className="divider-line my-6" />

          <div className="flex justify-between items-center mb-6">
            <span className="text-lg font-bold">Total Amount</span>
            <span className="text-2xl font-display font-black text-[#00F0FF]">$75 USD</span>
          </div>

          <button
            onClick={confirmFee}
            disabled={submitting}
            className="btn-primary w-full justify-center text-base py-3"
          >
            {submitting ? "Processing..." : "Confirm & Proceed to Checkout"} <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>

        <div className="text-center">
          <button
            onClick={() => navigate(`/accommodation-choice${cartRegId ? `?cart_reg_id=${cartRegId}` : ""}`)}
            className="text-sm text-[#94A3B8] hover:text-white transition-colors"
          >
            Wait, I'd rather book official accommodation
          </button>
        </div>
      </div>
    </div>
  );
}
